import "./Map.css";
import { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import MapMarker from "./MapMarker";
import API_SERVICE from "../../../utils/apiService";
import { Provider, useDispatch, useSelector } from "react-redux";
import store, {
  setActiveMarkerId,
  setMapBounds,
  setMapCenter,
  setMapZoom,
  setPlaces,
} from "../../../store";
import { BrowserRouter } from "react-router-dom";
import CreatePlaceButton from "../CreatePlaceButton";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const useInitializeGoogleMapsApi = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const scriptRef = useRef(null);

  useEffect(() => {
    console.log("Loading Google Maps script...");

    // Function to check if the script is already loaded
    const isScriptLoaded = () => {
      return window.google && window.google.maps && window.google.maps.Map;
    };

    // If already loaded, mark as loaded
    if (isScriptLoaded()) {
      console.log("Google Maps already loaded");
      setScriptLoaded(true);
      return;
    }

    const loadGoogleMapsScript = (apiKey) => {
      return new Promise((resolve, reject) => {
        // Don't add another script tag if one exists
        if (document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]')) {
          const checkIfLoaded = () => {
            if (isScriptLoaded()) {
              console.log("Existing Google Maps script loaded");
              resolve();
            } else {
              setTimeout(checkIfLoaded, 100);
            }
          };
          checkIfLoaded();
          return;
        }

        // Omit the API key when running on localhost or 127.0.0.1 to save on billing costs in development mode
        const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
        let scriptUrl = "https://maps.googleapis.com/maps/api/js?libraries=marker,places&callback=initMap";
        if (!isLocalhost) {
          scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=marker,places&callback=initMap`;
        }

        // Define a global callback function that Google Maps will call
        window.initMap = () => {
          console.log("Google Maps initialized via callback");
          resolve();
        };

        // Create and append the script
        const script = document.createElement("script");
        scriptRef.current = script;
        script.src = scriptUrl;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          console.log("Google Maps script tag loaded");
        };
        script.onerror = (err) => {
          console.error("Error loading Google Maps script:", err);
          reject(err);
        };
        document.head.appendChild(script);
      });
    };

    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY)
      .then(() => {
        console.log("Google Maps script loaded successfully");
        setScriptLoaded(true);
      })
      .catch((err) => console.error("Error loading Google Maps script:", err));

    return () => {
      // Clean up the callback
      delete window.initMap;
    };
  }, []);

  return scriptLoaded;
};

const useInitializeMap = (scriptLoaded) => {
  const mapRef = useRef(null); // Reference to the map container
  const mapInstance = useRef(null); // Reference to the map instance
  const [mapReady, setMapReady] = useState(false); // track if map is initialized
  const dispatch = useDispatch();
  const mapCenter = useSelector((state) => state.map.mapCenter); // Get map center from store
  const timeoutRef = useRef(null);

  useEffect(() => {
    // Clean up function to handle component unmounting
    return () => {
      console.log("Map component unmounting, cleaning up resources");
      if (mapInstance.current) {
        // Remove event listeners
        window.google?.maps?.event?.clearInstanceListeners?.(mapInstance.current);
        mapInstance.current = null;
      }

      // Clear any pending timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    console.log("Map initialization effect running. Script loaded:", scriptLoaded, "Map instance:", !!mapInstance.current);

    const setListener_updateMapAttributesAsMapChanges = () => {
      window.google.maps.event.addListener(mapInstance.current, "idle", () => {
        if (!mapReady) {
          setMapReady(true); // Set map as ready after first idle event
          console.log("Map is now ready");
        }
        const bounds = mapInstance.current.getBounds()?.toJSON();
        if (bounds) {
          dispatch(setMapBounds(bounds));
        }
        dispatch(setMapZoom(mapInstance.current.getZoom()));
        dispatch(setMapCenter(mapInstance.current.getCenter().toJSON()));
      });
    };

    const setListener_closeMarkerTooltipWhenMapClicked = () => {
      mapInstance.current.addListener("click", () => {
        dispatch(setActiveMarkerId(null));
      });
    };

    const initializeMap = () => {
      if (!scriptLoaded) {
        console.log("Script not loaded yet, skipping map initialization");
        return false;
      }

      if (mapInstance.current) {
        console.log("Map already initialized, updating properties");
        return true;
      }

      if (!mapRef.current) {
        console.log("Map container ref not available yet");
        return false;
      }

      console.log("Initializing new map instance");
      // Use center from Redux store if valid, otherwise default to Los Angeles
      const center = (mapCenter && mapCenter.lat !== 0)
        ? { lat: mapCenter.lat, lng: mapCenter.lng }
        : { lat: 34.0549076, lng: -118.242643 };

      // Initialize the map
      const mapOptions = {
        center,
        zoom: 12,
        mapId: "8c2b4adc293ca582",
        disableDefaultUI: true, // Disables all UI elements
        gestureHandling: "greedy", // fixes the "use two fingers to move the map" error on mobile
      };

      try {
        mapInstance.current = new window.google.maps.Map(
          mapRef.current,
          mapOptions,
        );
        setListener_updateMapAttributesAsMapChanges();
        setListener_closeMarkerTooltipWhenMapClicked();
        console.log("Map initialized successfully with center:", center);
        return true;
      } catch (err) {
        console.error("Error initializing map:", err);
        return false;
      }
    };

    // Try to initialize map on each render if needed
    const initialized = initializeMap();

    // If map couldn't be initialized, try again after a short delay
    if (!initialized && !timeoutRef.current) {
      console.log("Setting up delayed map initialization");
      timeoutRef.current = setTimeout(() => {
        console.log("Attempting delayed map initialization");
        initializeMap();
        timeoutRef.current = null;
      }, 500);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [scriptLoaded, mapReady, dispatch, mapCenter]);

  return { mapRef, mapInstance, mapReady };
};

const useFetchPlacesAsMapMoves = () => {
  const dispatch = useDispatch();
  const mapBounds = useSelector((state) => state.map.mapBounds);
  const acceptsReservations = useSelector(
    (state) => state.filter.acceptsReservations,
  );
  const hasWineGlasses = useSelector((state) => state.filter.hasWineGlasses);
  const places = useSelector((state) => state.place.places);
  useEffect(() => {
    console.log("Fetch places effect running. Map bounds:", mapBounds);
    if (!mapBounds || mapBounds.north === 0) {
      console.log("Skipping fetch places - invalid bounds");
      return;
    }
    (async () => {
      const markerList = await API_SERVICE.searchPlaces(
        mapBounds,
        acceptsReservations,
        hasWineGlasses,
      );
      dispatch(setPlaces(markerList));
    })();
  }, [mapBounds, acceptsReservations, hasWineGlasses, dispatch]);
  return places;
};

const useUpdateMapLocation = (scriptLoaded, mapReady, mapInstance) => {
  const mapCenter = useSelector((state) => state.map.mapCenter);
  const mapZoom = useSelector((state) => state.map.mapZoom);
  useEffect(() => {
    if (!scriptLoaded || !mapReady) {
      return;
    }
    mapInstance.current.setCenter(
      new window.google.maps.LatLng(mapCenter.lat, mapCenter.lng),
    );
    mapInstance.current.setZoom(mapZoom);
  }, [mapReady, scriptLoaded, mapInstance, mapCenter, mapZoom]);
};

const useRenderMarkers = (scriptLoaded, mapReady, places, mapInstance) => {
  const hasRenderedBefore = useRef({});
  const currentlyRenderedMarkers = useRef({});
  useEffect(() => {
    if (!scriptLoaded || !mapReady) {
      return;
    }
    const intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add("drop");
          intersectionObserver.unobserve(entry.target);
        }
      }
    });
    function createMarker(markerData) {
      const placeId = markerData.placeId;
      if (!mapReady) {
        return; // prevents premature loading
      }
      if (placeId in currentlyRenderedMarkers.current) {
        return; // prevents rendering again if currently rendered
      }

      var marker;
      if (placeId in hasRenderedBefore.current) {
        marker = hasRenderedBefore.current[placeId].advancedMarkerElement;
      } else {
        marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: {
            lat: markerData.locationLat,
            lng: markerData.locationLng,
          },
          map: mapInstance.current,
          title: markerData.name,
          content: document.createElement("div"), // placeholder
        });
        hasRenderedBefore.current[placeId] = {
          advancedMarkerElement: marker,
        };
      }
      const root = ReactDOM.createRoot(marker.content);
      root.render(
        <Provider store={store}>
          <BrowserRouter>
            <MapMarker markerData={markerData} />
          </BrowserRouter>
        </Provider>,
      );
      marker.addListener("click", () => {}); // to allow click events to propagate to children
      const content = marker.content;
      currentlyRenderedMarkers.current[placeId] = {
        root: root,
      };

      if (placeId in hasRenderedBefore.current) {
        return; // ensure markers only drop once
      }
      content.style.opacity = "0";
      content.addEventListener("animationend", (_event) => {
        content.classList.remove("drop");
        content.style.opacity = "1";
      });

      const time = Math.random(); // 2s delay for easy to see the animation
      content.style.setProperty("--delay-time", time + "s");
      intersectionObserver.observe(content);
    }
    places.forEach((markerData) => {
      createMarker(markerData);
    });

    /**
     * Unmount everything upon cleanup trigger.
     * This should only trigger when the places_list updates.
     * This is so that we don't have a unlimited growing list of <gmp*> DOM nodes.
     */
    return () => {
      Object.keys(currentlyRenderedMarkers.current).forEach(
        (currentlyRenderedPlaceId) => {
          let root =
            currentlyRenderedMarkers.current[currentlyRenderedPlaceId]["root"];
          root.unmount();
        },
      );
      currentlyRenderedMarkers.current = {};
    };
  }, [scriptLoaded, mapReady, places, mapInstance]);
};

function Map() {
  const scriptLoaded = useInitializeGoogleMapsApi();
  const { mapRef, mapInstance, mapReady } = useInitializeMap(scriptLoaded);

  // Additional debugging for map container size
  useEffect(() => {
    if (mapRef.current) {
      const { clientWidth, clientHeight } = mapRef.current;
      console.log("Map container dimensions:", {
        clientWidth,
        clientHeight,
        offsetWidth: mapRef.current.offsetWidth,
        offsetHeight: mapRef.current.offsetHeight,
        style: mapRef.current.style.cssText,
      });

      // If map container has no dimensions, try to force them
      if (clientWidth === 0 || clientHeight === 0) {
        console.log("Map container has zero dimensions - forcing size");
        mapRef.current.style.width = '100%';
        mapRef.current.style.height = '500px';
        mapRef.current.style.minHeight = '500px';
        mapRef.current.style.display = 'block';
      }
    }
  }, [mapRef]);

  const places = useFetchPlacesAsMapMoves();
  useUpdateMapLocation(scriptLoaded, mapReady, mapInstance);
  useRenderMarkers(scriptLoaded, mapReady, places, mapInstance);

  return (
    <div className="map-container" style={{ display: 'flex', flex: 1, minHeight: '500px' }}>
      <div className="map" ref={mapRef} style={{ flex: 1, minHeight: '500px' }}></div>
      <div className="map-add-button-container">
        <CreatePlaceButton
          sx={{ zIndex: 1000 }}
        />
      </div>
    </div>
  );
}

export default Map;
